<script setup lang="ts">
import {
    computed,
    ref,
    watch,
} from 'vue';

import { useAppointments } from '@/composables';
import { $t } from '@/i18n';
import { hospitalState } from '@/localStorage';
import { analytics } from '@/scitylana';
import { navigateTo } from '@/url';
import { isHSL } from '@/util';

import PetAppointmentCard from '../appointments/PetAppointmentCard.vue';
import TruButton from '../TruButton.vue';
import TruCard from '../TruCard.vue';
import TruLink from '../TruLink.vue';

import boxHeart from '../../assets/images/box-heart.svg';
import edoPuppyAndKitten from '../../assets/images/edo-puppy-and-kitten.webp';

const MAX_CARDS = 3;
const STD_DESKTOP_WIDTH = 1300;

const body = ref('');
const immediateInsurance = ref('');
const specialStates = [
    'CA',
    'ID',
    'NY',
    'PA',
    'PR',
    'SC',
    'WA',
];

const appointments = createAppointmentTiles();

const pets = computed(() => {
    const maxPetcards = (screen.width < STD_DESKTOP_WIDTH) ? MAX_CARDS - 1 : MAX_CARDS;

    return (appointments.value).slice(0, maxPetcards);
});

function createAppointmentTiles() {
    if (isHSL()) {
        const appointments = ref([]);
        return appointments;
    }
    else {
        const { appointments } = useAppointments(true); // true: limit for Dashboard
        return appointments;
    }
}

const stateRetries = ref(0);

// Wait until localStorage is populated before setting state-specific information.
watch([hospitalState, stateRetries], ([stateCode, tries], _, onCleanup) => {
    const state = stateCode == null ? null : stateCode.toUpperCase();

    if (state) {
        if (specialStates.includes(state)) {
            body.value = $t('dashboard.edos.html.bodyCaIdPaPrWaScNyVpl');
            immediateInsurance.value = $t('dashboard.edos.html.immediateInsuranceNyCaIdPaPrWaScVpl');
        } else {
            body.value = $t('dashboard.edos.html.body');
            immediateInsurance.value = $t('dashboard.edos.html.immediateInsurance');
        }
    } else {
        const blinker = `<div class="dashboard-edos-blinker">${ ' .'.repeat(tries % 8) }</div>`;

        body.value = immediateInsurance.value = blinker;

        const retry = window.setTimeout(() => {
            stateRetries.value += 1;
        }, 30);

        onCleanup(() => window.clearTimeout(retry));
    }
}, { immediate: true });

function gotoLearnMore() {
    analytics.home.edos.learnMore();

    window.open('https://trupanion.com/veterinarians/certificate-faqs');
}

function gotoNewOffer() {
    analytics.home.edos.startNewOffer();

    navigateTo('/Certificate');
}

function gotoSendExamDayOffer() {
    analytics.home.edos.sendExamDayOffer();

    navigateTo('/Certificate');
}

function gotoSendOffer(pet) {
    analytics.home.edos.sendOffer(pet);
}
</script>

<template>
    <div
        class="dashboard-edos"
        :class="{ 'mb-6': !isHSL()}"
    >
        <TruCard :header="$t('dashboard.edos.header')">
            <div class="content flex flex-col">
                <div class="main flex px-6 pt-6">
                    <div class="text">
                        <div
                            class="desktop-h4 mb-5"
                            v-html="immediateInsurance"
                        />
                        <div
                            class="desk-body-reg mb-5"
                            v-html="body"
                        />
                    </div>

                    <img
                        class="kitty ml-8 self-center"
                        :src="edoPuppyAndKitten"
                        :alt="$t('dashboard.edos.altText')"
                    >
                </div>

                <div class="learn-more flex justify-between items-center pt-5 px-6 pb-6">
                    <TruLink
                        size="xs"
                        @click="gotoLearnMore"
                    >
                        {{ $t('common.learnMore') }}
                    </TruLink>

                    <TruButton
                        class="send-exam-day-offer-btn"
                        size="md"
                        @click="gotoSendExamDayOffer"
                    >
                        {{ $t('common.sendExamDayOffer') }}
                    </TruButton>
                </div>

                <div
                    v-if="!isHSL()"
                    class="edo-section flex flex-col px-2"
                >
                    <hr class="mb-4">

                    <div class="edo-title desk-body-bold mb-3">
                        {{ $t('dashboard.edos.title') }}
                    </div>
                    <div class="edo-cards flex gap-2">
                        <PetAppointmentCard
                            v-for="pet in pets"
                            :key="pet.PimsPatientId + pet.AppointmentDate"
                            :pet="pet"
                            @click-primary="gotoSendOffer(pet)"
                        />

                        <div class="new-offer-card flex flex-col items-center">
                            <img
                                :src="boxHeart"
                                class="w-18 box-img"
                                :alt="$t('dashboard.edos.altBoxHeart')"
                            >

                            <div class="text mobi-cap1-italic new-offer-info">
                                {{ $t('dashboard.edos.newOffer') }}
                            </div>

                            <TruLink
                                size="xs"
                                @click="gotoNewOffer"
                            >
                                {{ $t('dashboard.edos.startNewOffer') }}
                            </TruLink>
                        </div>
                    </div>
                </div>
            </div>
        </TruCard>
    </div>
</template>

<style scoped lang="scss">
@use '../../scss/variables' as *;

.dashboard-edos {
    hr {
        color: var(--tru-silver);
    }

    :deep(.tru-card-body) {
        padding: 0;
    }

    .main {
        img {
            border-radius: 50%;
            width: 28rem;
        }
    }

    .send-exam-day-offer-btn {
        margin-right: 2.5rem;

        &> :deep(button) {
            font-size: 1.5rem;
        }
    }

    .edo-cards {
        height: 26.5rem;

        .appointment-card-wrapper {
            min-height: 32.5rem;
            width: calc(25% - 1rem);
        }

        .pet-appointment-card {
            height: fit-content;
        }

        .new-offer-card {
            background-color: var(--tru-white);
            border: 1px dashed var(--tru-web-body);
            border-radius: 1.5rem;
            min-height: 29.5rem;
            height: fit-content;
            width: calc(25% - 1rem);

            .box-img {
                padding-top: 3rem;
                padding-bottom: 2rem;
            }

            .new-offer-info {
                padding-bottom: 2rem;
                padding-left: 2rem;
                padding-right: 1rem;
            }

            .tru-link {
                padding: 0 1rem 1rem 1rem;
            }
        }
    }
}

:deep(.dashboard-edos-blinker) {
    width: 12rem;
}
</style>
